import React, { useEffect, useRef, useState } from 'react';
import { useMedia } from '../../context/MediaContext';
import { confirmVoice, getVoiceParagraph, verifyVoice } from '../../../ai-interview/utils/aiInterviewApi';
import { createRecognizer } from '../../../ai-interview/hooks/useSpeech';

/** Voice verification: only the passing read-aloud selected on Continue is stored as verified. */
const concatPcm = (chunks) => {
  let total = 0;
  for (const c of chunks) total += c.byteLength;
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) { out.set(new Uint8Array(c), off); off += c.byteLength; }
  return out.buffer;
};

const VoiceCheck = ({ ids, onDone }) => {
  const { restartMic, setFrameSink, capture } = useMedia();
  const [para, setPara] = useState(null);     // { index, text }
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const [selectedPass, setSelectedPass] = useState(null);
  const [paragraphBusy, setParagraphBusy] = useState(false);
  const chunksRef = useRef([]);
  const captionRef = useRef('');
  const captionSourceRef = useRef('unknown');
  const recognizerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    getVoiceParagraph().then((p) => { if (alive) setPara(p); }).catch(() => setErr('Could not load a passage.'));
    return () => {
      alive = false;
      setFrameSink(null);
      try { recognizerRef.current && recognizerRef.current.abort(); } catch { /* ignore */ }
      recognizerRef.current = null;
    };
  }, [setFrameSink]);

  const start = async () => {
    if (busy || recording) return;
    setErr('');
    setResult(null);
    setBusy(true);
    try { await restartMic(); } catch { setErr('Microphone unavailable. Check your browser or device settings, then retry.'); setBusy(false); return; }
    chunksRef.current = [];
    captionRef.current = '';
    captionSourceRef.current = 'unknown';
    capture.resetTurnMetrics();
    setFrameSink((buf) => chunksRef.current.push(buf));
    const rec = createRecognizer((text) => { captionRef.current = text || ''; }, null);
    recognizerRef.current = rec;
    if (rec?.start('', capture.getStream()?.getAudioTracks()[0])) {
      captionSourceRef.current = rec.getAudioSource();
    }
    setRecording(true);
    setBusy(false);
  };

  const changePassage = async () => {
    if (busy || recording || paragraphBusy || !para) return;
    setParagraphBusy(true);
    setErr('');
    try {
      const next = await getVoiceParagraph((para.index + 1 + Math.floor(Math.random() * (para.total - 1))) % para.total);
      setPara(next);
      setResult(null);
    } catch { setErr('Could not load a new passage. Please retry.'); }
    finally { setParagraphBusy(false); }
  };

  const confirm = async () => {
    if (!selectedPass?.attempt_id || busy) return;
    setBusy(true);
    setErr('');
    try {
      await confirmVoice(ids.companyId, ids.sessionId, ids.candidateId, selectedPass.attempt_id);
      onDone({ ok: true, lowQuality: false });
    } catch (e) { setErr(e.message || 'Could not save the voice check. Please retry.'); }
    finally { setBusy(false); }
  };

  const stop = async () => {
    setRecording(false);
    setFrameSink(null);
    try { recognizerRef.current && recognizerRef.current.stop(); } catch { /* ignore */ }
    recognizerRef.current = null;
    const chunks = chunksRef.current; chunksRef.current = [];
    const browserCaption = captionRef.current.trim();
    if (!chunks.length) { setErr('No audio captured — please try again.'); return; }
    setBusy(true);
    try {
      const metrics = capture.getMetrics();
      const response = await verifyVoice(ids.companyId, ids.sessionId, ids.candidateId,
        concatPcm(chunks), para ? para.index : -1, 'en', browserCaption, captionSourceRef.current, {
          raw_peak: Number(metrics.rawPeak?.toFixed(3)) || 0,
          processed_peak: Number(metrics.processedPeak?.toFixed(3)) || 0,
          raw_clipped: !!metrics.rawClipped,
          device_hint: metrics.deviceHint || 'unknown',
          track_rate: metrics.settings?.sampleRate || null,
          echo_cancellation: metrics.settings?.echoCancellation ?? null,
          noise_suppression: metrics.settings?.noiseSuppression ?? null,
          auto_gain_control: metrics.settings?.autoGainControl ?? null,
        });
      setResult({ ...response, browserCaption });
      if (response?.passed) setSelectedPass(response);
      if (!response?.passed) {
        const reason = response?.reason;
        setErr(reason === 'too_quiet' || reason === 'no_transcript'
          ? 'Your speech was not captured clearly. Move a neckband microphone closer to your mouth, or switch microphones in your device or browser settings and retry.'
          : reason === 'clipped'
            ? 'The microphone audio was distorted. Lower its input level in your device settings and retry.'
            : reason === 'transcription_unavailable'
              ? 'Speech recognition is temporarily unavailable. Please retry.'
              : 'The transcript did not match enough of the passage. Please read it again. If earbuds sound unclear, try another microphone in your device or browser settings.');
      }
    } catch (e) { setErr(e.message || 'Could not check the recording. Please retry.'); }
    finally { setBusy(false); }
  };

  const level = Math.min(1, capture.level * 1.6);

  return (
    <div className="mx-auto max-w-xl">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-slate-900/40">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Please read aloud</p>
        <p className="text-lg leading-relaxed">{para ? para.text : 'Loading a passage…'}</p>
      </div>

      <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
        Keep your microphone near your mouth. If a neckband or earbuds sound unclear, switch microphones in your device or browser settings, then retry.
      </p>

      {recording && (
        <div className="mt-4 h-3 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
          <div className="h-full rounded-full bg-gradient-to-r from-rose-400 to-primary-500 transition-[width] duration-75"
            style={{ width: `${Math.round(level * 100)}%` }} />
        </div>
      )}
      {err && <p className="mt-2 text-center text-xs text-rose-500">{err}</p>}
      {result && <div role="status" className={`mt-3 rounded-lg border px-4 py-3 text-sm ${result.passed
        ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-100'
        : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100'}`}>
        <p className="font-semibold">{result.passed ? 'Voice and microphone check passed.' : 'Voice and microphone check needs another try.'}</p>
        {result.transcript && <p className="mt-1 break-words">Server heard: “{result.transcript}”</p>}
        {!result.passed && result.browserCaption && <p className="mt-1 break-words">Browser heard: “{result.browserCaption}”</p>}
      </div>}
      {selectedPass && !result?.passed && <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
        Your earlier passing sample is still available. Continue with it, or try this passage again.
      </p>}

      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {recording ? (
          <button type="button" onClick={stop}
            className="rounded-full bg-rose-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-rose-700">
            Stop &amp; submit
          </button>
        ) : selectedPass ? (
          <>
            <button type="button" onClick={start} disabled={busy || paragraphBusy}
              className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium disabled:opacity-50">Retest</button>
            <button type="button" onClick={changePassage} disabled={busy || paragraphBusy || para?.total < 2}
              className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium disabled:opacity-50">Change passage</button>
            <button type="button" onClick={confirm} disabled={busy || paragraphBusy}
              className="rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
              {busy ? 'Saving…' : 'Continue'}
            </button>
          </>
        ) : (
          <>
          <button type="button" onClick={start} disabled={busy || !para}
            className="rounded-full bg-primary-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 dark:bg-violet-600 dark:hover:bg-violet-500">
            {busy ? 'Checking…' : result ? 'Retry reading' : 'Start reading'}
          </button>
          {result && <button type="button" onClick={changePassage} disabled={busy || paragraphBusy || para?.total < 2}
            className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium disabled:opacity-50">Change passage</button>}
          </>
        )}
      </div>
    </div>
  );
};

export default VoiceCheck;
